import {
  Grid,
  TextField,
  Button,
  Box,
  useMediaQuery,
  Select,
  MenuItem,
  FormControl,
  FormHelperText,
  InputLabel,
  Card,
  Accordion,
  Typography,
  AccordionSummary,
  AccordionDetails,
  Autocomplete
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Controller, useForm } from "react-hook-form";
import PropTypes from "prop-types";

import rotDecklists from "./rotation-decklists";
import unlDecklists from './unlimited-decklists'
import { ROTATION } from "../../constants";
import FormatButtons from "../format-buttons/FormatButtons";

const GameForm = ({ format, onSubmit, onFormatChange }) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const {
    register,
    handleSubmit,
    setValue,
    control,
    formState: { errors },
  } = useForm({ defaultValues: { deck: "", opponent: "" } });

  const resetForm = () => {
    setValue("opponent", "")
    setValue("order", "")
    setValue("result", "")
    setValue("notes", "")
  };

  const handleFormSubmit = async (data) => {
    data.format = format

    await onSubmit(data);
    resetForm();
  };

  const handleFormatClick = (formatValue) => {
    onFormatChange(formatValue)
    setValue("deck", "")
    resetForm()
  }

  return (
    <Card sx={{ marginBottom: 4 }}>
      <Accordion defaultExpanded square>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">Record a Game</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <FormatButtons format={format} onFormatClick={handleFormatClick} />

          <form onSubmit={handleSubmit(handleFormSubmit)}>
            <Grid container spacing={2} mt={0.01}>
              <Grid item xs={12} md={3}>
                <Controller
                  name='deck'
                  control={control}
                  render={({ field }) => (
                    <Autocomplete
                      {...field}
                      options={format === ROTATION ? rotDecklists : unlDecklists}
                      freeSolo
                      autoHighlight
                      autoSelect
                      fullWidth
                      renderInput={(params) =>
                        <TextField
                          {...params}
                          label='Deck'
                          size='small'
                          error={!!errors.deck}
                          helperText={errors.deck && errors.deck.message}
                        />
                      }
                      onChange={(_, data) => field.onChange(data)}
                    />
                  )}
                  rules={{
                    required: "Please enter the deck you used.",
                    maxLength: {
                      value: 50,
                      message:
                        "Please limit you the deck name to less than 50 characters.",
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12} md={3}>
                <Controller
                  name='opponent'
                  control={control}
                  render={({ field }) => (
                    <Autocomplete
                      {...field}
                      options={format === ROTATION ? rotDecklists : unlDecklists}
                      freeSolo
                      autoHighlight
                      autoSelect
                      fullWidth
                      renderInput={(params) =>
                        <TextField
                          {...params}
                          label='Opponent'
                          size='small'
                          error={!!errors.opponent}
                          helperText={errors.opponent && errors.opponent.message}
                        />
                      }
                      onChange={(_, data) => field.onChange(data)}
                    />
                  )}
                  rules={{
                    required: "Please enter the deck your opponent used.",
                    maxLength: {
                      value: 50,
                      message:
                        "Please limit the deck name to less than 50 characters.",
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth size="small" error={!!errors.order}>
                  <InputLabel>Order</InputLabel>
                  <Controller
                    control={control}
                    name="order"
                    defaultValue=""
                    rules={{ required: "Select if you went 1st or 2nd." }}
                    render={({ field }) => (
                      <Select label="Order" {...field}>
                        <MenuItem value="1st">1st</MenuItem>
                        <MenuItem value="2nd">2nd</MenuItem>
                      </Select>
                    )}
                  />
                  {errors.order && (
                    <FormHelperText>{errors.order.message}</FormHelperText>
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth size="small" error={!!errors.result}>
                  <InputLabel>Result</InputLabel>
                  <Controller
                    control={control}
                    name="result"
                    defaultValue=""
                    rules={{ required: "Select match result." }}
                    render={({ field }) => (
                      <Select label="Result" {...field}>
                        <MenuItem value="WIN">WIN</MenuItem>
                        <MenuItem value="LOSE">LOSE</MenuItem>
                      </Select>
                    )}
                  />
                  {errors.result && (
                    <FormHelperText>{errors.result.message}</FormHelperText>
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Notes"
                  size="small"
                  fullWidth
                  multiline
                  rows={2}
                  error={!!errors.notes}
                  helperText={errors.notes && errors.notes.message}
                  {...register("notes", {
                    maxLength: {
                      value: 256,
                      message:
                        "Limit your notes to 256 characters or less",
                    },
                  })}
                />
              </Grid>
            </Grid>
            <Box
              sx={{
                display: "flex",
                flexDirection: isSmallScreen ? "column-reverse" : "row",
                justifyContent: 'space-between',
                marginTop: 1,
              }}
            >
              <Button
                color="info"
                fullWidth
                sx={{
                  marginTop: isSmallScreen ? 0.5 : 0,
                  marginRight: isSmallScreen ? 0 : 1,
                }}
                type="reset"
                onClick={resetForm}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="primary"
                fullWidth
                sx={{
                  marginBottom: isSmallScreen ? 1 : 0,
                  marginLeft: isSmallScreen ? 0 : 1,
                }}
                type="submit"
              >
                Submit
              </Button>
            </Box>
          </form>
        </AccordionDetails>
      </Accordion>
    </Card>
  );
};

GameForm.propTypes = {
  format: PropTypes.string,
  onFormatChange: PropTypes.func,
  onSubmit: PropTypes.func,
};

export default GameForm;
